'use client'

import {Dispatch, MouseEventHandler, SetStateAction, useEffect, useState} from 'react'
import { cva } from 'class-variance-authority'

import {cn, Stack} from '@/lib/utils'
import {MainMenuItemModel} from "@/components/custom/header/mainmenu/mainMenuItem";
import IconChevronRight from "@/icons/chevron-right.svg";
import Link from "next/link";
import {MobileBreadcrumb} from "@/components/custom/header/mainmenu/mobileMenu/mobileBreadcrumb";

const mobileVariants = cva(
    'absolute inset-0 left-0 top-full z-10 h-svh origin-top flex-col justify-start overflow-hidden bg-gray-beige-light transition-all duration-500 lg:hidden',
    {
        variants: {
            open: {
                true: 'max-h-svh translate-y-0 opacity-100',
                false: 'max-h-0 translate-y-6 opacity-0'
            }
        },
        defaultVariants: {
            open: false
        }
    }
)

interface MobileMenuProps {
    menuItems: MainMenuItemModel[];
    mainMenuOpen: boolean;
    setMainMenuOpen: Dispatch<SetStateAction<boolean>>
}

interface MobileMenuEntryProps {
    item: MainMenuItemModel,
    lastSelectedItems: MainMenuItemModel[],
    onClick: MouseEventHandler<HTMLDivElement>,
    setMainMenuOpen: Dispatch<SetStateAction<boolean>>
}

function MobileMenuEntry({ item, lastSelectedItems, onClick, setMainMenuOpen }: MobileMenuEntryProps) {
    let url = "";
    if(lastSelectedItems.length <= 0 || item.fullPath) {
        url = item.url;
    } else {
        for(let i = 0; i < lastSelectedItems.length; i++) {
            url += lastSelectedItems[i]?.url + "/";
        }
        let itemUrl = item.url;
        if(item.level === 3) itemUrl = itemUrl.split("/")[1];
        url += itemUrl;
    }

    return (
        <li key={item.id} className='flex flex-col'>
            <div className='link border-b-gray-beige-dark/50 flex flex-row border-b'>
                <Link href={url} className='flex-1 px-8 py-2 text-gray-beige-dark hover:text-secondary' onClick={() => setMainMenuOpen(false)}>
                    {item.text}
                </Link>
                <span
                    className='opener flex-0 border-l-gray-beige-dark/50 flex cursor-pointer items-center justify-center border-l px-4 py-2'
                    onClick={onClick}
                >
                <IconChevronRight/>
              </span>
            </div>
        </li>
    )
}

const MobileMenu = ({ menuItems, mainMenuOpen, setMainMenuOpen }: MobileMenuProps) => {
    const [itemStack] = useState(new Stack<MainMenuItemModel[]>())
    const [history, setHistory] = useState<MainMenuItemModel[][]>([menuItems])
    const activeItems = history[history.length - 1];
    const [lastSelectedItems, setLastSelectedItems] = useState<MainMenuItemModel[]>([]);

    function handleItemClick(item: MainMenuItemModel) {
        if(item.children && item.children.length > 0) {
            itemStack.push(activeItems);
            setHistory([...history, item.children]);
            setLastSelectedItems([...lastSelectedItems, item]);
        }
    }

    return (
        <div aria-label='Mobile Main Menu' className={cn(mobileVariants({open: mainMenuOpen}))}>
            <MobileBreadcrumb itemStack={itemStack} history={history} setHistory={setHistory} lastSelectedItems={lastSelectedItems} setLastSelectedItems={setLastSelectedItems} />
            <div className='relative h-full transition-transform duration-700'>
                    <ul className={`list absolute left-0 top-0 z-10 h-full w-full`}>
                        {activeItems.map((menuItem: MainMenuItemModel) =>
                            <MobileMenuEntry
                                key={menuItem.id}
                                item={menuItem} onClick={() => handleItemClick(menuItem)}
                                lastSelectedItems={lastSelectedItems}
                                setMainMenuOpen={setMainMenuOpen}
                            />
                        )}
                    </ul>
                </div>
        </div>
    )
}

export {
    MobileMenu
}
